export const EMPLOYEE_ASSIGNABLE_ROLES = [
  "Teacher",
  "Staff",
  "Choreographer",
] as const;

export type EmployeeAssignableRole = (typeof EMPLOYEE_ASSIGNABLE_ROLES)[number];

export type EmployeeRoleChoice = "Teacher" | "Staff" | "Both";

export function employeeHasTeacherRole(
  roles: readonly string[] | null | undefined,
): boolean {
  return roles?.includes("Teacher") ?? false;
}

export function employeeHasStaffRole(
  roles: readonly string[] | null | undefined,
): boolean {
  return roles?.includes("Staff") ?? false;
}

export function employeeHasChoreographerRole(
  roles: readonly string[] | null | undefined,
): boolean {
  return roles?.includes("Choreographer") ?? false;
}

export function employeeSelectionFromRoles(
  roles: readonly string[] | null | undefined,
): EmployeeAssignableRole[] {
  return EMPLOYEE_ASSIGNABLE_ROLES.filter((role) => roles?.includes(role));
}

export function employeeRoleChoiceFromRoles(
  roles: readonly string[] | null | undefined,
): EmployeeRoleChoice {
  const isTeacher = employeeHasTeacherRole(roles);
  const isStaff = employeeHasStaffRole(roles);
  if (isTeacher && isStaff) return "Both";
  if (isStaff) return "Staff";
  return "Teacher";
}

export function employeeApiRoleToSelection(
  role: EmployeeRoleChoice,
): EmployeeAssignableRole[] {
  if (role === "Both") return ["Teacher", "Staff"];
  return [role];
}

/** Legacy single value. Choreographer is carried on `roles`, not this string. */
export function employeeSelectionToApiRole(
  roles: readonly string[],
): EmployeeRoleChoice | null {
  const isTeacher = roles.includes("Teacher");
  const isStaff = roles.includes("Staff");
  if (isTeacher && isStaff) return "Both";
  if (isTeacher) return "Teacher";
  if (isStaff) return "Staff";
  return null;
}

export function employeeRolesForApi(
  roles: readonly string[],
): EmployeeAssignableRole[] {
  return employeeSelectionFromRoles(roles);
}

export function sameEmployeeRoles(
  left: readonly string[],
  right: readonly string[],
): boolean {
  const a = employeeRolesForApi(left);
  const b = employeeRolesForApi(right);
  return a.length === b.length && a.every((role, index) => role === b[index]);
}

/** Keeps a saved Choreographer role when the plugin hides that checkbox. */
export function employeeRolesForSave(
  selected: readonly string[],
  current: readonly string[] | null | undefined,
  portalEnabled: boolean,
): EmployeeAssignableRole[] {
  const next = employeeRolesForApi(selected);
  if (
    !portalEnabled &&
    employeeHasChoreographerRole(current) &&
    !next.includes("Choreographer")
  ) {
    return employeeRolesForApi([...next, "Choreographer"]);
  }
  return next;
}

export function employeeRoleIncludesTeacher(roles: readonly string[]): boolean {
  return roles.includes("Teacher");
}

export function employeeRoleBadgeLabel(
  isTeacher: boolean,
  isStaff: boolean,
  isChoreographer = false,
): string {
  const labels: string[] = [];
  if (isStaff) labels.push("Staff");
  if (isTeacher) labels.push("Teacher");
  if (isChoreographer) labels.push("Choreographer");
  if (labels.length === 0) return "Staff";
  if (labels.length === 1) return labels[0] ?? "Staff";
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels[0]}, ${labels[1]}, and ${labels[2]}`;
}
